/**
 * Invoices, the payment method and prepaid credit — `modules/billing/routes.ts`.
 *
 * Three of the five writes here carry `money: true`, and the two that do not are
 * the reason the flag is per call rather than per module: choosing a rail and
 * forgetting a card move no money, and adding an `Idempotency-Key` to a `PUT` that
 * is already idempotent would be ceremony.
 */

import type { Money, PaymentRail } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'
import type { DetailOptions } from './requests.js'

/** The rails a hosted page can be opened for. `wise` is a transfer, not a page. */
export type ChargeableRail = 'card' | 'ach' | 'sepa'

/** `GET /api/clients/me/invoices` — the ledger on `client/billing.html` §7. */
export function useInvoices() {
  return useApiQuery({
    key: queryKeys.billing.invoices(),
    request: (api) => api.rest.clients.me.invoices.$get({ query: {} }),
  })
}

/** `GET /api/clients/me/invoices/:ref` — every line and every payment. */
export function useInvoice(ref: string, options: DetailOptions = {}) {
  return useApiQuery({
    key: queryKeys.billing.invoice(ref),
    request: (api) => api.rest.clients.me.invoices[':ref'].$get({ param: { ref } }),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/**
 * `GET /api/clients/me/invoices/next-estimate` — "the next one is issued 1 Sep".
 *
 * **This `staleTime` is the whole of the hour `.docs/14-billing-invoices.md` §6 asks
 * for.** The figure is a dry run of the whole billing run in a transaction that is
 * thrown away, so it is not asked for again on every focus — but it is a money figure
 * on a page with a refetch, so the hour has to live somewhere a `refetch()` and a
 * mutation's invalidation can both reach. It used to be `Cache-Control:
 * private, max-age=3600` on the route as well, which is the one store neither can:
 * session 24 (R3-08) found `/app/billing` printing an hour-old estimate from a world
 * that no longer existed, through the very `refetchEstimate` that was there to stop
 * it. The route now sends `private, no-store` (`billing/routes.ts`).
 */
export function useNextEstimate() {
  return useApiQuery({
    key: queryKeys.billing.nextEstimate(),
    request: (api) => api.rest.clients.me.invoices['next-estimate'].$get(),
    staleTime: 60 * 60 * 1000,
  })
}

/** `POST /api/clients/me/invoices/:ref/pay` — opens the hosted page. */
export function usePayInvoice() {
  return useApiMutation({
    module: 'billing',
    money: true,
    request: (api, variables: { ref: string; rail: ChargeableRail }, options) =>
      api.rest.clients.me.invoices[':ref'].pay.$post(
        { param: { ref: variables.ref }, json: { rail: variables.rail } },
        { headers: options.headers },
      ),
  })
}

/** `GET /api/clients/me/payment-method` — the card or mandate on file, masked. */
export function usePaymentMethod(options: { readonly inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.billing.paymentMethod(),
    request: (api) => api.rest.clients.me['payment-method'].$get(),
    // Same argument as `useWidgets`: `#payments` is an anchor, and a section that
    // disappears on a failed read takes it with it.
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/**
 * `POST /api/clients/me/payment-method/setup` — Checkout in setup mode.
 *
 * Not money: nothing is charged, and the method arrives by webhook rather than from
 * the browser. What comes back is a URL to send the reader to.
 */
export function useStartPaymentMethodSetup() {
  return useApiMutation({
    module: 'billing',
    request: (api, rail: ChargeableRail) =>
      api.rest.clients.me['payment-method'].setup.$post({ json: { rail } }),
  })
}

/** `DELETE /api/clients/me/payment-method` — forgets it. Answers `204`. */
export function useForgetPaymentMethod() {
  return useApiMutation({
    module: 'billing',
    request: (api) => api.rest.clients.me['payment-method'].$delete(),
  })
}

/** `PUT …/payment-method/rail-preference` — the three radios on settings. */
export function useSetRailPreference() {
  return useApiMutation({
    module: 'billing',
    request: (api, rail: PaymentRail) =>
      api.rest.clients.me['payment-method']['rail-preference'].$put({ json: { rail } }),
  })
}

/** `POST /api/clients/me/credits/top-up` — buying prepaid credit. */
export function useTopUpCredits() {
  return useApiMutation({
    module: 'billing',
    money: true,
    request: (api, variables: { amount: Money; rail: ChargeableRail }, options) =>
      api.rest.clients.me.credits['top-up'].$post(
        { json: { amount: variables.amount, rail: variables.rail } },
        { headers: options.headers },
      ),
    alsoInvalidates: ['usage'],
  })
}
